import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ShoppingBag, Search, Heart, Phone, MapPin, Instagram, Facebook,
  Minus, Plus, Trash2, Send, Monitor, Tablet, Smartphone, CheckCircle2,
  Zap, Eye, Star, PackageCheck, CreditCard, Banknote, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "../lib/api";
import { discountPct } from "../lib/cart";
import { themeVars, headingFont, SafeImg, SectionHead, ProductCard, StoreHero, Stars } from "../components/StoreKit";
import { Marquee } from "../components/Shared";
import SiteRenderer from "../components/SiteRenderer";
import RESite from "./realestate/RESite";
import EditorCanvas from "../editor/EditorCanvas";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const SAMPLE_REVIEWS = [
  { name: "Ananya R.", rating: 5, text: "Exceeded expectations — premium quality and fast delivery." },
  { name: "Vikram M.", rating: 4.5, text: "Great value for the price. Packaging was excellent." },
  { name: "Priya N.", rating: 5, text: "This is my second order. Consistently great experience." },
];

export default function ThemePreview() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState({ name: "home" }); // home | product | checkout | confirm
  const [device, setDevice] = useState("desktop");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [wish, setWish] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [selection, setSelection] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", address: "", pincode: "", city: "", state: "" });
  const [method, setMethod] = useState("cod");
  const [placed, setPlaced] = useState(null);

  useEffect(() => {
    setData(null); setView({ name: "home" }); setCart([]); setCat(""); setQ("");
    api.get(`/public/theme-preview/${tid}`).then((r) => setData(r.data)).catch(() => setError("Theme not found"));
  }, [tid]);

  const theme = data?.theme;
  const store = data?.store;
  const colors = store?.settings?.colors || theme?.colors || {};
  const vars = useMemo(() => themeVars(colors, theme), [colors, theme]);
  const HS = theme?.header_style || "solid";
  const headerStyle =
    HS === "glass" ? { backgroundColor: "rgba(255,255,255,0.85)", color: "var(--sp)", backdropFilter: "blur(16px)" }
    : HS === "dark" ? { backgroundColor: "#0B1220", color: "#fff" }
    : HS === "gradient" ? { backgroundImage: "linear-gradient(90deg, var(--sp), var(--sa))", color: "#fff" }
    : HS === "minimal" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "1px solid rgba(0,0,0,0.08)" }
    : HS === "centered" ? { backgroundColor: "var(--sbg)", color: "var(--sp)", borderBottom: "2px solid var(--sp)" }
    : { backgroundColor: "var(--sp)", color: "#fff" };
  const headerTextIsLight = ["solid", "dark", "gradient"].includes(HS);
  const radius = theme?.radius || "10px";
  const head = headingFont(theme);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  // Real Estate themes render the dedicated RE reference-design demo
  if (theme?.re) return <div data-testid="re-theme-preview"><RESite demoTheme={theme} /></div>;

  const info = store.business_info;
  const filtered = data.products.filter((p) => (!cat || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const featured = filtered.slice(0, 4);
  const deals = filtered.filter((p) => discountPct(p) > 0);
  const count = cart.reduce((s, c) => s + c.qty, 0);
  const subtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 49;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  const addToCart = (p, n = 1, variation = "") => {
    const key = p.id + "|" + variation;
    setCart((c) => {
      const ex = c.find((x) => x.key === key);
      return ex ? c.map((x) => (x.key === key ? { ...x, qty: x.qty + n } : x))
        : [...c, { key, id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "", qty: n, variation }];
    });
    toast.success(`${p.name} added to cart`);
  };
  const buyNow = (p, n = 1, variation = "") => { addToCart(p, n, variation); setView({ name: "checkout" }); window.scrollTo(0, 0); };
  const toggleWish = (p) => {
    const has = wish.some((w) => w.id === p.id);
    setWish((w) => has ? w.filter((x) => x.id !== p.id) : [...w, { id: p.id, name: p.name, price: p.price, image: p.images?.[0] || "" }]);
    toast.success(has ? "Removed from wishlist" : "Saved to wishlist");
  };
  const openProduct = (p) => {
    const sel = {};
    (p.variations || []).forEach((v) => { sel[v.name] = v.options?.[0] || ""; });
    setSelection(sel); setImg(0); setQty(1);
    setView({ name: "product", p }); window.scrollTo(0, 0);
  };
  const placeDemoOrder = (e) => {
    e.preventDefault();
    setPlaced({ order_number: `DEMO-${Math.floor(10000 + Math.random() * 89999)}`, total, items: cart, method });
    setCart([]); setView({ name: "confirm" }); window.scrollTo(0, 0);
  };

  const deviceWidth = device === "desktop" ? "100%" : device === "tablet" ? 820 : 400;

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950" data-testid="theme-preview">
      {/* ===== Preview toolbar ===== */}
      <div className="fixed bottom-4 inset-x-0 z-50 flex justify-center px-4 pointer-events-none">
        <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="pointer-events-auto glass-card rounded-full shadow-2xl px-3 py-2 flex items-center gap-2 flex-wrap justify-center glow">
          <button data-testid="preview-back" onClick={() => navigate("/partner/websites/new", { state: { theme_id: tid } })}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold hover:bg-muted transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Themes
          </button>
          <div className="hidden sm:flex items-center gap-1 border border-border rounded-full p-1">
            {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
              <button key={d} data-testid={`preview-device-${d}`} onClick={() => setDevice(d)}
                className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${device === d ? "gradient-bg text-white" : "text-muted-foreground"}`}>
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
          <span className="hidden md:flex items-center gap-1.5 text-xs font-bold px-2 text-muted-foreground">
            <Eye className="h-3.5 w-3.5" /> {theme.name} · {theme.category} · Live Demo
          </span>
          <button data-testid="preview-use-theme" onClick={() => navigate("/partner/websites/new", { state: { theme_id: tid } })}
            className="px-5 py-2 rounded-full text-xs font-extrabold text-white gradient-bg glow-sm active:scale-95 transition-transform">
            Use This Theme
          </button>
        </motion.div>
      </div>

      {/* ===== Device frame ===== */}
      <div className="mx-auto transition-all duration-500 pb-24" style={{ maxWidth: deviceWidth }}>
        <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className={`min-h-screen bg-[var(--sbg)] text-[var(--st)] shadow-2xl ${device !== "desktop" ? "rounded-2xl overflow-hidden my-4 border border-black/10" : ""}`}>

          {/* Header */}
          <div className="text-center text-[11px] font-bold tracking-wide py-2 px-4 text-white"
            style={{ backgroundImage: "linear-gradient(90deg, var(--sp), var(--sa))" }}>
            {data.products.length > 0 ? "Free shipping over ₹999 · Demo store — orders are simulated" : `${theme.name} · ${theme.category} · Live demo website`}
          </div>
          <header className="sticky top-0 z-40 shadow-sm" style={headerStyle}>
            <div className={`max-w-7xl mx-auto px-4 h-16 flex items-center gap-3 ${HS === "centered" ? "justify-center" : ""}`}>
              <button onClick={() => setView({ name: "home" })} className="flex items-center gap-2.5 min-w-0">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center font-display font-extrabold text-sm shrink-0" style={{ backgroundColor: "var(--sa)" }}>{store.name[0]}</div>
                <span data-testid="preview-store-name" className={`${head} text-lg tracking-tight truncate`}>{store.name}</span>
              </button>
              {data.products.length > 0 && (
              <div className="hidden md:flex flex-1 max-w-md mx-4 relative">
                <Search className={`h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 ${headerTextIsLight ? "text-white/50" : "text-zinc-400"}`} />
                <input data-testid="preview-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
                  className={`w-full h-10 pl-10 pr-4 rounded-full text-sm outline-none ${headerTextIsLight ? "bg-white/15 placeholder-white/60 text-white" : "bg-black/5 placeholder-zinc-400 text-zinc-900"}`} />
              </div>
              )}
              {data.products.length > 0 && (
              <div className="ml-auto flex items-center gap-1.5">
                <button data-testid="preview-wishlist-button" onClick={() => setWishOpen(true)} className={`relative h-10 w-10 rounded-full flex items-center justify-center transition-colors ${headerTextIsLight ? "hover:bg-white/15" : "hover:bg-black/5"}`}>
                  <Heart className="h-4 w-4" />
                  {wish.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full text-[9px] font-extrabold flex items-center justify-center text-white" style={{ backgroundColor: "var(--sa)" }}>{wish.length}</span>}
                </button>
                <button data-testid="preview-cart-button" onClick={() => setCartOpen(true)}
                  className="relative h-10 px-4 rounded-full flex items-center gap-2 font-bold text-sm text-white active:scale-95 transition-transform" style={{ backgroundColor: "var(--sa)" }}>
                  <ShoppingBag className="h-4 w-4" /> <span className="hidden sm:inline">Cart</span>
                  {count > 0 && <span data-testid="preview-cart-count" className="h-5 min-w-5 px-1 rounded-full bg-white text-[10px] font-extrabold flex items-center justify-center" style={{ color: "var(--sp)" }}>{count}</span>}
                </button>
              </div>
              )}
            </div>
            {data.products.length > 0 && (
            <nav className={`border-t ${headerTextIsLight ? "border-white/10" : "border-black/5"}`}>
              <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto py-2">
                <button onClick={() => { setCat(""); setView({ name: "home" }); }} className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${!cat ? "text-white" : "opacity-70"}`} style={!cat ? { backgroundColor: "var(--sa)" } : {}}>All</button>
                {data.categories.map((c) => (
                  <button key={c} data-testid={`preview-cat-${c.toLowerCase()}`} onClick={() => { setCat(c); setView({ name: "home" }); }}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${cat === c ? "text-white" : "opacity-70"}`} style={cat === c ? { backgroundColor: "var(--sa)" } : {}}>{c}</button>
                ))}
              </div>
            </nav>
            )}
          </header>

          <AnimatePresence mode="wait">
            {/* ===== HOME ===== */}
            {view.name === "home" && (
              <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <StoreHero theme={theme} store={store} info={info} products={data.products} head={head} radius={radius} />
                <Marquee items={data.products.length > 0 ? ["FREE SHIPPING OVER ₹999", "NEW ARRIVALS WEEKLY", "EASY RETURNS", "SECURE PAYMENTS"] : [theme.category.toUpperCase(), "TRUSTED & VERIFIED", "PREMIUM QUALITY", "TOP RATED"]} />

                {theme?.site && <SiteRenderer site={theme.site} head={head} radius={radius} info={info} store={store} theme={theme} defaults={data.content_defaults?.services ? data.content_defaults : null} />}

                {!theme?.site && data.content_defaults?.services && (
                <div data-testid="default-service-pricing">
                  <EditorCanvas doc={{ sections: [data.content_defaults.services, data.content_defaults.pricing].filter(Boolean) }} theme={theme} store={store} />
                </div>
                )}

                {featured.length > 0 && (
                <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
                  <SectionHead overline="Handpicked for you" title="Featured Products" />
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
                    {featured.map((p, i) => (
                      <ProductCard key={p.id} p={p} delay={i * 0.05} radius={radius}
                        onOpen={() => openProduct(p)} onAdd={() => addToCart(p)} onBuy={() => buyNow(p)}
                        wished={wish.some((w) => w.id === p.id)} onWish={() => toggleWish(p)} />
                    ))}
                  </div>
                </section>
                )}

                {deals.length > 0 && (
                  <section id="deals" className="py-14" style={{ backgroundColor: "var(--ss)" }}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6">
                      <SectionHead overline="Limited time" title="Deals of the Day" />
                      <div className="flex gap-5 overflow-x-auto pb-4 mt-8 snap-x">
                        {deals.map((p) => (
                          <motion.div key={p.id} whileHover={{ y: -6 }} onClick={() => openProduct(p)}
                            className="snap-start shrink-0 w-60 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow cursor-pointer">
                            <div className="relative h-40 overflow-hidden">
                              <SafeImg src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                              <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white" style={{ backgroundColor: "var(--sa)" }}>{discountPct(p)}% OFF</span>
                            </div>
                            <div className="p-4">
                              <p className="font-bold text-sm truncate">{p.name}</p>
                              <div className="flex items-baseline gap-2 mt-1">
                                <span className="font-display font-extrabold" style={{ color: "var(--sp)" }}>{fmtINR(p.price)}</span>
                                <span className="text-xs line-through text-zinc-400">{fmtINR(p.compare_price)}</span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </section>
                )}

                {!theme?.site && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
                  <SectionHead overline="Demo reviews" title="What Customers Say" />
                  <div className="grid sm:grid-cols-3 gap-5 mt-8">
                    {SAMPLE_REVIEWS.map((r, i) => (
                      <div key={r.name} className="bg-white border border-black/5 p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-300" style={{ borderRadius: radius }}>
                        <Stars rating={r.rating} />
                        <p className="text-sm leading-relaxed mt-3" style={{ color: "var(--st)", opacity: 0.8 }}>"{r.text}"</p>
                        <p className="text-xs font-extrabold mt-4" style={{ color: "var(--sp)" }}>{r.name} · Verified Buyer</p>
                      </div>
                    ))}
                  </div>
                </section>
                )}

                {!theme?.site && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
                  <div className="rounded-2xl p-10 text-center text-white relative overflow-hidden" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                    <h2 className="font-display font-extrabold text-3xl tracking-tighter">Get offers before anyone else</h2>
                    <form onSubmit={(e) => { e.preventDefault(); toast.success("Subscribed! (demo)"); }} className="max-w-md mx-auto mt-6 flex gap-2">
                      <Input type="email" required placeholder="you@email.com" className="h-12 rounded-full bg-white/95 text-zinc-900 border-0" data-testid="preview-newsletter" />
                      <button className="h-12 px-6 rounded-full bg-zinc-900 text-white font-bold text-sm shrink-0 active:scale-95 transition-transform"><Send className="h-4 w-4" /></button>
                    </form>
                  </div>
                </section>
                )}

                {!theme?.site && (
                <footer style={{ backgroundColor: "var(--sp)" }} className="text-white">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-8">
                    <div>
                      <p className={`${head} text-lg`}>{store.name}</p>
                      <p className="text-sm text-white/60 mt-2">{info.description}</p>
                      <div className="flex gap-2 mt-4">
                        <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Instagram className="h-4 w-4" /></span>
                        <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Facebook className="h-4 w-4" /></span>
                      </div>
                    </div>
                    <div className="text-sm text-white/75 space-y-2">
                      <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-3">Contact</p>
                      <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{info.phone}</p>
                      <p>{info.email}</p>
                      <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5" />{info.address}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-3">Categories</p>
                      <div className="flex flex-wrap gap-2">{data.categories.map((c) => <span key={c} className="text-xs bg-white/10 px-3 py-1.5 rounded-full">{c}</span>)}</div>
                    </div>
                  </div>
                  <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {new Date().getFullYear()} {store.name} · Demo preview powered by MILTROO</div>
                </footer>
                )}
              </motion.div>
            )}

            {/* ===== PRODUCT DETAIL ===== */}
            {view.name === "product" && (
              <motion.div key="product" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="preview-product">
                <button onClick={() => setView({ name: "home" })} className="flex items-center gap-2 text-sm font-bold mb-8 hover:opacity-70 transition-opacity"><ArrowLeft className="h-4 w-4" /> Back to store</button>
                <div className="grid lg:grid-cols-2 gap-10">
                  <div className="relative overflow-hidden bg-zinc-100" style={{ borderRadius: radius }}>
                    <SafeImg src={view.p.images?.[img] || view.p.images?.[0]} alt={view.p.name} className="w-full aspect-square object-cover" />
                    {discountPct(view.p) > 0 && <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-extrabold text-white" style={{ backgroundColor: "var(--sa)" }}>{discountPct(view.p)}% OFF</span>}
                  </div>
                  <div>
                    <p className="uppercase text-[11px] tracking-[0.25em] font-extrabold" style={{ color: "var(--sa)" }}>{view.p.category}</p>
                    <h1 className={`${head} text-3xl sm:text-4xl tracking-tight mt-2`} style={{ color: "var(--sp)" }}>{view.p.name}</h1>
                    <div className="flex items-center gap-2 mt-3"><Stars rating={view.p.rating ?? 4.5} /><span className="text-xs font-semibold opacity-50">{view.p.rating ?? 4.5} · {view.p.reviews_count ?? 64} reviews</span></div>
                    <div className="flex items-baseline gap-3 mt-5">
                      <span className="font-display font-extrabold text-4xl" style={{ color: "var(--sp)" }}>{fmtINR(view.p.price)}</span>
                      {view.p.compare_price > view.p.price && <span className="text-lg line-through opacity-40">{fmtINR(view.p.compare_price)}</span>}
                    </div>
                    <p className="text-sm leading-relaxed mt-5 opacity-75">{view.p.description}</p>
                    {(view.p.variations || []).map((v) => (
                      <div key={v.name} className="mt-5">
                        <p className="text-xs font-extrabold uppercase tracking-widest mb-2 opacity-60">{v.name}: <span style={{ color: "var(--sp)" }}>{selection[v.name]}</span></p>
                        <div className="flex flex-wrap gap-2">
                          {v.options.map((opt) => (
                            <button key={opt} onClick={() => setSelection({ ...selection, [v.name]: opt })}
                              className={`px-4 py-2 text-sm font-bold border-2 transition-all active:scale-95 ${selection[v.name] === opt ? "text-white" : ""}`}
                              style={selection[v.name] === opt ? { backgroundColor: "var(--sp)", borderColor: "var(--sp)", borderRadius: radius } : { borderColor: "rgba(0,0,0,0.12)", borderRadius: radius }}>
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 mt-6">
                      <div className="flex items-center border-2 rounded-full" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center"><Minus className="h-4 w-4" /></button>
                        <span className="w-8 text-center font-extrabold">{qty}</span>
                        <button onClick={() => setQty(qty + 1)} className="h-11 w-11 flex items-center justify-center"><Plus className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button data-testid="preview-add-to-cart" onClick={() => addToCart(view.p, qty, Object.values(selection).join(", "))}
                        className="flex-1 py-3.5 rounded-full font-bold text-sm border-2 flex items-center justify-center gap-2 active:scale-95 transition-transform"
                        style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>
                        <ShoppingBag className="h-4 w-4" /> Add to Cart
                      </button>
                      <button data-testid="preview-buy-now" onClick={() => buyNow(view.p, qty, Object.values(selection).join(", "))}
                        className="flex-1 py-3.5 rounded-full font-bold text-sm text-white flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg"
                        style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                        <Zap className="h-4 w-4" /> Buy Now
                      </button>
                    </div>
                    <p className="flex items-center gap-1.5 text-xs font-bold mt-4" style={{ color: "#16A34A" }}><ShieldCheck className="h-4 w-4" /> In stock · demo preview</p>
                  </div>
                </div>
                <div className="mt-14">
                  <SectionHead overline="Demo reviews" title={`Rated ${view.p.rating ?? 4.5} out of 5`} />
                  <div className="grid sm:grid-cols-3 gap-5 mt-6">
                    {SAMPLE_REVIEWS.map((r) => (
                      <div key={r.name} className="bg-white border border-black/5 p-5" style={{ borderRadius: radius }}>
                        <Stars rating={r.rating} /><p className="text-sm mt-2 opacity-75">"{r.text}"</p>
                        <p className="text-xs font-extrabold mt-3" style={{ color: "var(--sp)" }}>{r.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ===== CHECKOUT ===== */}
            {view.name === "checkout" && (
              <motion.div key="checkout" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="preview-checkout">
                <button onClick={() => setView({ name: "home" })} className="flex items-center gap-2 text-sm font-bold mb-8 hover:opacity-70 transition-opacity"><ArrowLeft className="h-4 w-4" /> Continue shopping</button>
                {cart.length === 0 ? (
                  <p className="text-center py-20 opacity-60 text-sm">Cart is empty — add a product first.</p>
                ) : (
                  <form onSubmit={placeDemoOrder} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-10">
                    <div className="space-y-6">
                      <h1 className={`${head} text-3xl`} style={{ color: "var(--sp)" }}>Checkout <span className="text-sm font-sans font-bold opacity-50">(demo)</span></h1>
                      <div className="bg-white rounded-xl border border-black/5 p-6 space-y-4">
                        <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sa)" }}>Contact & Address</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-1"><Label className="text-xs font-bold">Full name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="preview-co-name" /></div>
                          <div className="space-y-1"><Label className="text-xs font-bold">Phone *</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1"><Label className="text-xs font-bold">Address *</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                        <div className="grid sm:grid-cols-3 gap-3">
                          <div className="space-y-1"><Label className="text-xs font-bold">Pincode *</Label><Input required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
                          <div className="space-y-1"><Label className="text-xs font-bold">City *</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                          <div className="space-y-1"><Label className="text-xs font-bold">State *</Label><Input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl border border-black/5 p-6 space-y-3">
                        <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sa)" }}>Payment method</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <button type="button" data-testid="preview-pay-cod" onClick={() => setMethod("cod")}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${method === "cod" ? "border-[var(--sp)]" : "border-black/10"}`}>
                            <Banknote className="h-5 w-5" style={{ color: "var(--sp)" }} /><div><p className="text-sm font-extrabold">Cash on Delivery</p><p className="text-xs opacity-55">Pay on arrival</p></div>
                          </button>
                          <button type="button" data-testid="preview-pay-online" onClick={() => setMethod("online")}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${method === "online" ? "border-[var(--sp)]" : "border-black/10"}`}>
                            <CreditCard className="h-5 w-5" style={{ color: "var(--sp)" }} /><div><p className="text-sm font-extrabold">Pay Online</p><p className="text-xs opacity-55">UPI · Cards · PhonePe</p></div>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-black/5 h-fit p-6 space-y-3">
                      <p className="font-display font-extrabold" style={{ color: "var(--sp)" }}>Order Summary</p>
                      {cart.map((c) => (
                        <div key={c.key} className="flex justify-between text-sm"><span className="truncate pr-2">{c.name} × {c.qty}</span><span className="font-bold shrink-0">{fmtINR(c.price * c.qty)}</span></div>
                      ))}
                      <div className="border-t border-black/10 pt-3 space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="opacity-60">Subtotal</span><span className="font-bold">{fmtINR(subtotal)}</span></div>
                        <div className="flex justify-between"><span className="opacity-60">Shipping</span><span className="font-bold">{shipping ? fmtINR(shipping) : "Free"}</span></div>
                        <div className="flex justify-between"><span className="opacity-60">Tax (5%)</span><span className="font-bold">{fmtINR(tax)}</span></div>
                        <div className="flex justify-between pt-2 border-t border-black/10"><span className="font-extrabold">Total</span><span className="font-display font-extrabold text-xl" style={{ color: "var(--sp)" }}>{fmtINR(total)}</span></div>
                      </div>
                      <button type="submit" data-testid="preview-place-order"
                        className="w-full py-4 rounded-full font-extrabold text-sm text-white active:scale-95 transition-transform shadow-lg"
                        style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                        Place Order · {fmtINR(total)}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            )}

            {/* ===== CONFIRMATION ===== */}
            {view.name === "confirm" && placed && (
              <motion.div key="confirm" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto py-24 px-6 text-center" data-testid="preview-confirm">
                <div className="h-20 w-20 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                  <PackageCheck className="h-9 w-9" />
                </div>
                <h1 className={`${head} text-3xl mt-6`} style={{ color: "var(--sp)" }}>Order confirmed!</h1>
                <p className="font-mono font-extrabold text-xl mt-2">{placed.order_number}</p>
                <p className="text-sm opacity-60 mt-2">Total {fmtINR(placed.total)} · {placed.method === "cod" ? "Cash on Delivery" : "Paid Online"}</p>
                <p className="text-xs mt-4 px-4 py-3 rounded-xl bg-amber-500/10 text-amber-700 font-bold">This is a theme preview — no real order was placed.</p>
                <button onClick={() => setView({ name: "home" })} className="mt-6 px-8 py-3 rounded-full font-bold text-sm text-white active:scale-95 transition-transform" style={{ backgroundColor: "var(--sp)" }}>Continue exploring</button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Cart drawer */}
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetContent className="w-full sm:max-w-md flex flex-col" style={vars}>
              <SheetHeader><SheetTitle className="font-display">Your Cart ({count})</SheetTitle></SheetHeader>
              <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                {cart.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">Your cart is empty</p>}
                {cart.map((c) => (
                  <div key={c.key} className="flex gap-3 items-center">
                    <div className="h-16 w-16 rounded-md bg-muted overflow-hidden shrink-0">{c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}</div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{c.name}</p><p className="text-xs text-muted-foreground">{fmtINR(c.price)}</p></div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCart((prev) => prev.map((x) => x.key === c.key ? { ...x, qty: Math.max(1, x.qty - 1) } : x))} className="h-7 w-7 rounded-full border border-border flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                      <span className="w-6 text-center text-sm font-bold">{c.qty}</span>
                      <button onClick={() => setCart((prev) => prev.map((x) => x.key === c.key ? { ...x, qty: x.qty + 1 } : x))} className="h-7 w-7 rounded-full border border-border flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                    </div>
                    <button onClick={() => setCart((prev) => prev.filter((x) => x.key !== c.key))} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <div className="border-t border-border pt-4 pb-5 mt-4 shrink-0 bg-background">
                  <div className="flex justify-between font-display font-extrabold text-lg mb-4"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
                  <motion.button whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
                    data-testid="preview-checkout-button" onClick={() => { setCartOpen(false); setView({ name: "checkout" }); window.scrollTo(0, 0); }}
                    className="w-full py-4 rounded-full font-extrabold text-[15px] tracking-wide text-white shadow-xl hover:shadow-2xl transition-shadow"
                    style={{ backgroundColor: "var(--sp)" }}>
                    Proceed to Checkout · {fmtINR(subtotal)}
                  </motion.button>
                </div>
              )}
            </SheetContent>
          </Sheet>

          {/* Wishlist drawer */}
          <Sheet open={wishOpen} onOpenChange={setWishOpen}>
            <SheetContent className="w-full sm:max-w-md" style={vars}>
              <SheetHeader><SheetTitle className="font-display">Wishlist ({wish.length})</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3">
                {wish.length === 0 && <p className="text-sm text-muted-foreground text-center py-12">Nothing saved yet — tap the heart on any product.</p>}
                {wish.map((w) => (
                  <div key={w.id} className="flex gap-3 items-center border border-border rounded-lg p-3">
                    <div className="h-14 w-14 rounded-md bg-muted overflow-hidden shrink-0">{w.image && <img src={w.image} alt="" className="h-full w-full object-cover" />}</div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{w.name}</p><p className="text-xs text-muted-foreground">{fmtINR(w.price)}</p></div>
                    <button onClick={() => { const p = data.products.find((x) => x.id === w.id); if (p) { addToCart(p); toggleWish(p); } }}
                      className="px-3.5 py-2 rounded-full text-xs font-bold text-white active:scale-95 transition-transform" style={{ backgroundColor: "var(--sp)" }}>Move to Cart</button>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}
